/**
 * What cooking a recipe does to the fridge.
 *
 * Until now the two screens didn't touch: you could cook everything in the
 * house and the inventory would still claim you had it. This closes that loop,
 * which is most of the difference between a list you maintain and a list that
 * maintains itself.
 *
 * The hard part is that a recipe never says *how much* it uses. "Chicken" might
 * be one of six thighs or all 500g. Rather than guess a number and be wrong,
 * this asks one question per item — did you finish it? — and only acts on the
 * answer.
 */

import type { FoodItem, Unit } from "../food/types";

/**
 * Units you can sensibly subtract one of. A weight can't be decremented
 * meaningfully — 500g of chicken minus "one" is nonsense — so those items are
 * only ever left alone or removed outright.
 */
const COUNTABLE: ReadonlySet<Unit> = new Set<Unit>(["pcs", "pack"]);

export function isCountable(unit: Unit): boolean {
  return COUNTABLE.has(unit);
}

/**
 * Whether to tick "finished it" for this item by default.
 *
 * A single packet or piece is almost always used up by the recipe that calls
 * for it; a kilo of rice almost never is. The default is a guess, which is why
 * it's shown as a toggle rather than applied silently.
 */
export function suggestUsedUp(item: FoodItem): boolean {
  return isCountable(item.unit) && item.quantity <= 1;
}

export interface CookChoice {
  id: string;
  /** True when the item is gone entirely. */
  usedUp: boolean;
}

export interface CookOutcome {
  /** The whole inventory as it should now be stored. */
  items: FoodItem[];
  /** Names of items removed, for the message afterwards. */
  removed: string[];
  /** Names of items whose count went down by one. */
  reduced: string[];
}

/**
 * Applies the choices to the inventory.
 *
 * Returns a complete new inventory rather than mutating, so the caller can keep
 * the old array and offer an undo. Items the recipe never touched pass through
 * untouched.
 */
export function applyCook(items: FoodItem[], choices: CookChoice[]): CookOutcome {
  const byId = new Map(choices.map((choice) => [choice.id, choice.usedUp]));
  const next: FoodItem[] = [];
  const removed: string[] = [];
  const reduced: string[] = [];

  for (const item of items) {
    const usedUp = byId.get(item.id);

    // Not part of this recipe.
    if (usedUp === undefined) {
      next.push(item);
      continue;
    }

    if (usedUp) {
      removed.push(item.name);
      continue;
    }

    // Kept, but one fewer if that means anything for this unit.
    if (isCountable(item.unit) && item.quantity > 1) {
      next.push({ ...item, quantity: item.quantity - 1 });
      reduced.push(item.name);
    } else {
      next.push(item);
    }
  }

  return { items: next, removed, reduced };
}

/** A sentence describing what just happened, or null when nothing did. */
export function describeCook(outcome: CookOutcome): string | null {
  const parts: string[] = [];
  if (outcome.removed.length > 0) {
    parts.push(
      outcome.removed.length === 1
        ? `Used up ${outcome.removed[0]}`
        : `Used up ${outcome.removed.length} items`
    );
  }
  if (outcome.reduced.length > 0) {
    parts.push(
      outcome.reduced.length === 1
        ? `took one from ${outcome.reduced[0]}`
        : `took one from ${outcome.reduced.length} more`
    );
  }
  if (parts.length === 0) return null;

  // "Used up Spinach, took one from Eggs."
  return `${parts.join(", ")}.`;
}
